/* AmountField — LED-02, LED-03, TRK-02 */
import { useId } from 'react';

import focus from '../primitives/focus.module.css';
import styles from './AmountField.module.css';

export interface AmountFieldProps {
  /** What the person has typed, in rupees, exactly as typed. Never a number. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label?: string;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
}

/**
 * The amount on `LED-02`.
 *
 * **A text input, not `type="number"`.** A number input on a phone shows a keypad with a spinner, allows
 * `e` and `-` and `+`, and silently reports an empty string for anything it considers invalid — so a
 * person typing `1.2.3` hands the app no way to say what was wrong. This is `inputMode="decimal"`, which
 * gets the numeric keypad `LED-02` asks for while leaving the text under the app's control.
 *
 * **The value is a string all the way through, and the caller converts it once with `parseAmount`.** The
 * component never holds a number, because a number cannot represent "1." — which is a real thing to be
 * mid-way through typing, and turning it into `1` under somebody's finger is the bug this avoids.
 *
 * The rupee sign sits outside the input rather than inside its value, so it cannot be deleted by a
 * backspace and never has to be stripped before parsing.
 */
export function AmountField({
  value,
  onChange,
  label = 'Amount',
  error,
  disabled = false,
  autoFocus = false,
}: AmountFieldProps): React.JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>

      <div
        className={[styles.box, error ? styles.invalid : '', disabled ? styles.disabled : '']
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.symbol} aria-hidden="true">
          ₹
        </span>
        <input
          id={id}
          className={[styles.input, focus.focusable].join(' ')}
          type="text"
          inputMode="decimal"
          // The keypad, and nothing an autofill would want to put here.
          autoComplete="off"
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            // Digits and at most one dot. Filtering here rather than refusing on submit means the field
            // never holds something the caller's `parseAmount` would have to reject, and a stray letter
            // from a predictive keyboard simply does not appear.
            const next = event.target.value.replace(/[^\d.]/g, '');
            const [rupees, ...rest] = next.split('.');

            onChange(rest.length === 0 ? next : `${rupees}.${rest.join('').slice(0, 2)}`);
          }}
        />
      </div>

      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
